import React from 'react';
import { STATUS_COLORS, STATUS_LABELS } from './weatherConstants';
import { EVENT_TYPES } from '../home/WeatherMapControls';
import HazardGlyph from './HazardGlyph';

/** Sentence-case labels for the map card legend (spec) — the shared
 *  STATUS_LABELS stay title-case for popups and the sidebar badges. */
const CARD_STATUS_LABELS = {
  producing: 'Producing',
  not_producing: 'Not producing',
  starting_soon: 'Starting soon',
  ending_soon: 'Ending soon',
};

/** Ship markers aren't harvest statuses, so they get their own legend entry. */
const IN_TRANSIT = { id: 'in_transit', color: '#2563EB', label: 'In transit' };

const LegendItem = ({ color, label, glyph, status }) => (
  <div className="flex items-center gap-[6px]">
    {glyph
      ? <HazardGlyph type={glyph} />
      : <div className="h-[8px] w-[8px] shrink-0 rounded-full" style={{ backgroundColor: color, opacity: status === 'not_producing' ? 0.55 : 1 }} />}
    <span className="font-['Inter'] whitespace-nowrap text-[12px] leading-[18px] text-[#5D625F] dark:text-[#828382]">
      {label}
    </span>
  </div>
);

/** Overlay treatment: each entry is its own translucent, blurred pill so the
 *  key stays readable while floating over the weather tiles. */
const OverlayItem = ({ color, label, glyph, status }) => (
  <div className="flex items-center gap-[6px] rounded-full border border-white/70 bg-white/70 px-[10px] py-[4px] shadow-[0_2px_10px_rgba(0,0,0,0.12)] backdrop-blur-[6px] dark:border-white/10 dark:bg-[#1d1d1d]/70">
    {glyph
      ? <HazardGlyph type={glyph} />
      : <div className="h-[8px] w-[8px] shrink-0 rounded-full" style={{ backgroundColor: color, opacity: status === 'not_producing' ? 0.55 : 1 }} />}
    <span className="font-['Inter'] whitespace-nowrap text-[11px] leading-[16px] text-[#5D625F] dark:text-[#A8ADA9]">
      {label}
    </span>
  </div>
);

/**
 * @param {'strip'|'card'|'overlay'} variant `overlay` is the Global Produce Map
 *   treatment — a row of translucent pills floating on the map itself; `card`
 *   is the earlier boxed tile that sat below the map; `strip` keeps the
 *   original full-width row used by the home WeatherMap.
 * @param {boolean} showStatuses Harvest statuses only key the producing-region
 *   pins, so the `growing` layer mode drops them when those pins are hidden.
 * @param {boolean} showInTransit Likewise for the vessel key: `growing` draws
 *   no shipments, so keying their colour there describes an empty map.
 */
const StatusLegend = ({
  activeEventTypes = [],
  variant = 'strip',
  showStatuses = true,
  showInTransit = true,
}) => {
  const events = EVENT_TYPES.filter((e) => activeEventTypes.includes(e.id));

  if (variant === 'overlay') {
    return (
      <div className="flex flex-wrap items-center justify-end gap-[6px]">
        {showStatuses && Object.entries(STATUS_COLORS).map(([status, color]) => (
          <OverlayItem key={status} status={status} color={color} label={CARD_STATUS_LABELS[status] || STATUS_LABELS[status]} />
        ))}
        {showInTransit && <OverlayItem color={IN_TRANSIT.color} label={IN_TRANSIT.label} />}
        {events.map((event) => (
          <OverlayItem key={event.id} color={event.color} glyph={event.id} label={event.label} />
        ))}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className="inline-flex flex-wrap items-center gap-x-[16px] gap-y-[6px] rounded-[8px] border border-[#E8EBE9] bg-white px-[14px] py-[8px] dark:border-[#30363d] dark:bg-[#1d1d1d]">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <LegendItem key={status} status={status} color={color} label={CARD_STATUS_LABELS[status] || STATUS_LABELS[status]} />
        ))}
        <LegendItem color={IN_TRANSIT.color} label={IN_TRANSIT.label} />
        {events.map((event) => (
          <LegendItem key={event.id} color={event.color} glyph={event.id} label={event.label} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2 border-t border-[#E8EBE9] dark:border-[#3a3c3b]">
      <div className="flex flex-wrap gap-3">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: color, opacity: status === 'not_producing' ? 0.55 : 1 }}
            />
            <span className="text-xs text-[#828382]">{STATUS_LABELS[status]}</span>
          </div>
        ))}
      </div>

      {events.length > 0 && (
        <>
          <div className="h-4 w-px bg-[#C3C4C3] dark:bg-[#828382]" />
          <div className="flex flex-wrap gap-3">
            {events.map((event) => (
              <div key={event.id} className="flex items-center gap-1.5">
                <HazardGlyph type={event.id} />
                <span className="text-xs text-[#828382]">{event.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default StatusLegend;
